import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PlayersAPI, TeamsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SiteHeader from "@/components/SiteHeader";
import PlayerQRCode from "@/components/PlayerQRCode";
import ImageInput from "@/components/ImageInput";
import SectionOrderCard from "@/components/SectionOrderCard";
import { SPORTS, SPORT_IDS, getSport } from "@/lib/sports";
import { useCategories } from "@/lib/categories";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const SectionTitle = ({ children }) => (
  <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight pt-2">
    {children}
  </h3>
);

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="text-overline !text-zinc-400">
      {label}
    </Label>
    {children}
  </div>
);

const emptyPlayer = {
  name: "",
  position: "",
  sport: "football",
  category: "",
  club_name: "",
  club_logo_url: "",
  team_id: "",
  season: "",
  cover_image_url: "",
  hero_image_url: "",
  primary_color: "#0066FF",
  stats: {},
  bio: "",
  date_of_birth: "",
  height_cm: "",
  weight_kg: "",
  jersey_number: "",
  achievements: [],
  highlights: [],
  gallery: [],
  helmet_image_url: "",
  race_results: [],
  section_order: [],
};

export default function Admin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyPlayer);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id && id !== "new");
  const [teams, setTeams] = useState([]);
  const isEdit = !!id && id !== "new";

  useEffect(() => {
    TeamsAPI.list().then(setTeams).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id || id === "new") return;
    PlayersAPI.get(id)
      .then((d) =>
        setForm({
          ...emptyPlayer,
          ...d,
          stats: { ...emptyPlayer.stats, ...(d.stats || {}) },
          height_cm: d.height_cm ?? "",
          weight_kg: d.weight_kg ?? "",
          jersey_number: d.jersey_number ?? "",
          achievements: d.achievements || [],
          highlights: d.highlights || [],
          gallery: d.gallery || [],
          race_results: d.race_results || [],
        }),
      )
      .catch(() => toast.error("Joueur introuvable"))
      .finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStat = (k, v) => setForm((f) => ({ ...f, stats: { ...f.stats, [k]: Number(v) || 0 } }));

  const addItem = (key, item) =>
    setForm((f) => ({ ...f, [key]: [...f[key], item] }));
  const updateItem = (key, idx, patch) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  const removeItem = (key, idx) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const motor = getSport(form.sport).position_scoring;
  const categories = useCategories();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.position?.trim()) {
      toast.error("Le nom et le poste sont requis");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      height_cm: form.height_cm === "" ? null : Number(form.height_cm),
      weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
      jersey_number: form.jersey_number === "" ? null : Number(form.jersey_number),
    };
    try {
      let result;
      if (isEdit) {
        result = await PlayersAPI.update(id, payload);
        toast.success("Joueur mis à jour");
      } else {
        result = await PlayersAPI.create(payload);
        toast.success("Joueur créé");
      }
      navigate(`/player/${result.slug}`);
    } catch (err) {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer ce joueur ?")) return;
    try {
      await PlayersAPI.remove(id);
      toast.success("Joueur supprimé");
      navigate("/");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-16 pb-mobile-safe">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body mb-6"
          data-testid="admin-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>

        <h1 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tighter leading-none mb-2">
          {isEdit ? "Éditer" : "Nouveau"} <span className="text-gradient-gold">joueur</span>
        </h1>
        <p className="text-zinc-400 font-body mb-10">
          Renseigne les infos, les stats, les highlights et la galerie. La page publique sera générée automatiquement.
        </p>

        {isEdit && form.slug && (
          <div className="mb-10">
            <PlayerQRCode
              url={`${window.location.origin}/player/${form.slug}`}
              title={form.name}
              subtitle={form.club_name ? `${form.club_name} · Saison ${form.season}` : `Saison ${form.season}`}
              fileBase={`player-${form.slug}`}
              primaryColor={form.primary_color || "#0066FF"}
            />
          </div>
        )}

        <form onSubmit={submit} className="space-y-8" data-testid="admin-form">
          <SectionTitle>Sport</SectionTitle>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {SPORT_IDS.map((s) => {
              const sport = SPORTS[s];
              const active = (form.sport || "football") === s;
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => set("sport", s)}
                  data-testid={`select-sport-${s}`}
                  data-active={active}
                  className="metal-pill px-3 py-2.5 rounded-md text-[10px] uppercase tracking-[0.22em] font-bold"
                  style={{ "--metal-accent": sport.color }}
                >
                  {sport.label}
                </button>
              );
            })}
          </div>

          {/* Identity */}
          <SectionTitle>Identité</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom complet *" htmlFor="name">
              <Input
                id="name"
                data-testid="input-name"
                className={inputClass}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Lucas Martin"
                required
              />
            </Field>
            <Field label="Poste *" htmlFor="position">
              <Input
                id="position"
                data-testid="input-position"
                list="position-suggestions"
                className={inputClass}
                value={form.position}
                onChange={(e) => set("position", e.target.value)}
                placeholder={getSport(form.sport).positions[0] || "Poste"}
                required
              />
              <datalist id="position-suggestions">
                {getSport(form.sport).positions.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </Field>
            <Field label="Catégorie" htmlFor="category">
              <Select value={form.category || ""} onValueChange={(v) => set("category", v)}>
                <SelectTrigger
                  id="category"
                  data-testid="select-category"
                  className={inputClass}
                >
                  <SelectValue placeholder="U17" />
                </SelectTrigger>
                <SelectContent className="bg-[#111216] border-white/10 text-white">
                  {form.category && !categories.includes(form.category) && (
                    <SelectItem value={form.category}>{form.category}</SelectItem>
                  )}
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={motor ? "N° de course" : "Numéro de maillot"} htmlFor="jersey">
              <Input
                id="jersey"
                data-testid="input-jersey"
                type="number"
                className={inputClass}
                value={form.jersey_number}
                onChange={(e) => set("jersey_number", e.target.value)}
                placeholder="10"
              />
            </Field>
          </div>

          {/* Club */}
          <SectionTitle>{motor ? "Écurie / Team & Couleurs" : "Club & Couleurs"}</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Équipe (page connectée)" htmlFor="team_id">
              <Select
                value={form.team_id || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") {
                    set("team_id", "");
                    return;
                  }
                  const t = teams.find((x) => x.id === v);
                  set("team_id", v);
                  if (t) {
                    // Auto-fill club_name + logo + color from selected team
                    setForm((f) => ({
                      ...f,
                      team_id: v,
                      club_name: t.club_name || t.name || f.club_name,
                      club_logo_url: t.logo_url || f.club_logo_url,
                      primary_color: t.primary_color || f.primary_color,
                      sport: t.sport || f.sport,
                    }));
                  }
                }}
              >
                <SelectTrigger
                  id="team_id"
                  data-testid="input-team-id"
                  className={inputClass}
                >
                  <SelectValue placeholder="Joueur individuel (sans équipe)" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">
                    Joueur individuel (sans équipe)
                  </SelectItem>
                  {teams.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      {t.category ? ` · ${t.category}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={motor ? "Écurie / Team (libellé affiché)" : "Nom du club (libellé affiché)"} htmlFor="club_name">
              <Input
                id="club_name"
                data-testid="input-club-name"
                className={inputClass}
                value={form.club_name}
                onChange={(e) => set("club_name", e.target.value)}
                placeholder={motor ? "Scuderia Léman Racing" : "Étoile Carouge FC"}
              />
            </Field>
            {motor && (
              <Field label="Photo du casque" htmlFor="helmet">
                <ImageInput
                  value={form.helmet_image_url}
                  onChange={(v) => set("helmet_image_url", v)}
                  testId="img-helmet"
                  size="md"
                  placeholder="https://..."
                />
              </Field>
            )}
            <Field label="Couleur de l'équipe" htmlFor="primary_color">
              <div className="flex gap-2 items-center">
                <Input
                  id="primary_color"
                  data-testid="input-primary-color"
                  type="color"
                  className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                  value={form.primary_color || "#0066FF"}
                  onChange={(e) => set("primary_color", e.target.value)}
                />
                <Input
                  className={inputClass}
                  value={form.primary_color || "#0066FF"}
                  onChange={(e) => set("primary_color", e.target.value)}
                  placeholder="#0066FF"
                />
              </div>
            </Field>
            <Field label="Saison" htmlFor="season">
              <Input
                id="season"
                data-testid="input-season"
                className={inputClass}
                value={form.season}
                onChange={(e) => set("season", e.target.value)}
                placeholder="2024/2025"
              />
            </Field>
            <Field label="Logo du club" htmlFor="club_logo_url">
              <ImageInput
                value={form.club_logo_url}
                onChange={(v) => set("club_logo_url", v)}
                testId="img-club-logo"
                size="sm"
                placeholder="https://..."
              />
            </Field>
            <Field label="Photo de couverture (fond hero)" htmlFor="cover">
              <ImageInput
                value={form.cover_image_url}
                onChange={(v) => set("cover_image_url", v)}
                testId="img-cover"
                aspect="wide"
                placeholder="https://..."
              />
            </Field>
            <Field label="Photo principale du joueur" htmlFor="hero">
              <ImageInput
                value={form.hero_image_url}
                onChange={(v) => set("hero_image_url", v)}
                testId="img-hero"
                aspect="wide"
                placeholder="https://..."
              />
            </Field>
          </div>

          {/* Stats */}
          <SectionTitle>Statistiques</SectionTitle>
          <p className="text-zinc-500 font-body text-sm -mt-4">
            Stats adaptées au sport sélectionné.
          </p>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${Math.min(getSport(form.sport).player_stats.length, 4)}, minmax(0, 1fr))`,
            }}
          >
            {getSport(form.sport).player_stats.map((s) => (
              <Field key={s.key} label={s.label} htmlFor={`stat-${s.key}`}>
                <Input
                  id={`stat-${s.key}`}
                  data-testid={`input-stat-${s.key}`}
                  type="number"
                  className={inputClass}
                  placeholder="0"
                  value={form.stats[s.key] || ""}
                  onChange={(e) => setStat(s.key, e.target.value)}
                />
              </Field>
            ))}
          </div>

          {/* Personal */}
          <SectionTitle>Informations personnelles</SectionTitle>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Date de naissance" htmlFor="dob">
              <Input
                id="dob"
                data-testid="input-dob"
                className={inputClass}
                value={form.date_of_birth}
                onChange={(e) => set("date_of_birth", e.target.value)}
                placeholder="14/03/2008"
              />
            </Field>
            <Field label="Taille (cm)" htmlFor="height">
              <Input
                id="height"
                data-testid="input-height"
                type="number"
                className={inputClass}
                value={form.height_cm}
                onChange={(e) => set("height_cm", e.target.value)}
                placeholder="178"
              />
            </Field>
            <Field label="Poids (kg)" htmlFor="weight">
              <Input
                id="weight"
                data-testid="input-weight"
                type="number"
                className={inputClass}
                value={form.weight_kg}
                onChange={(e) => set("weight_kg", e.target.value)}
                placeholder="68"
              />
            </Field>
          </div>
          <Field label="Biographie" htmlFor="bio">
            <Textarea
              id="bio"
              data-testid="input-bio"
              rows={4}
              className={inputClass}
              value={form.bio}
              onChange={(e) => set("bio", e.target.value)}
              placeholder="Présente le joueur en quelques lignes..."
            />
          </Field>

          {/* Highlights */}
          <SectionTitle>Highlights vidéo</SectionTitle>
          <p className="text-zinc-500 font-body text-sm -mt-4">
            YouTube, Vimeo ou lien direct vers un fichier vidéo (.mp4, .webm).
          </p>
          <div className="space-y-3">
            {form.highlights.map((h, i) => (
              <div
                key={h.id || i}
                className="grid sm:grid-cols-[1fr,1.5fr,140px,auto] gap-2 items-end bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`highlight-row-${i}`}
              >
                <Input
                  className={inputClass}
                  placeholder="Titre"
                  value={h.title || ""}
                  onChange={(e) => updateItem("highlights", i, { title: e.target.value })}
                />
                <Input
                  className={inputClass}
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={h.url || ""}
                  onChange={(e) => updateItem("highlights", i, { url: e.target.value })}
                />
                <Select
                  value={h.type || "youtube"}
                  onValueChange={(v) => updateItem("highlights", i, { type: v })}
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111216] border-white/10 text-white">
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="vimeo">Vimeo</SelectItem>
                    <SelectItem value="url">Fichier (URL)</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  onClick={() => removeItem("highlights", i)}
                  data-testid={`remove-highlight-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="add-highlight"
              onClick={() =>
                addItem("highlights", { id: crypto.randomUUID(), title: "", type: "youtube", url: "" })
              }
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une vidéo
            </Button>
          </div>

          {/* Gallery */}
          <SectionTitle>Galerie photos</SectionTitle>
          <div className="space-y-3">
            {form.gallery.map((g, i) => (
              <div
                key={g.id || i}
                className="grid sm:grid-cols-[auto,1fr,auto] gap-3 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`gallery-row-${i}`}
              >
                <ImageInput
                  value={g.url || ""}
                  onChange={(v) => updateItem("gallery", i, { url: v })}
                  testId={`img-gallery-${i}`}
                  size="md"
                  placeholder="URL de l'image"
                />
                <Input
                  className={inputClass}
                  placeholder="Légende (optionnelle)"
                  value={g.caption || ""}
                  onChange={(e) =>
                    updateItem("gallery", i, { caption: e.target.value })
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  onClick={() => removeItem("gallery", i)}
                  data-testid={`remove-gallery-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="add-gallery"
              onClick={() => addItem("gallery", { id: crypto.randomUUID(), url: "", caption: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une photo
            </Button>
          </div>

          {/* Achievements */}
          <SectionTitle>Palmarès</SectionTitle>
          <div className="space-y-3">
            {form.achievements.map((a, i) => (
              <div
                key={a.id || i}
                className="grid sm:grid-cols-[120px,1fr,1.5fr,auto] gap-2 items-end bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`achievement-row-${i}`}
              >
                <Input
                  className={inputClass}
                  placeholder="2024"
                  value={a.date || ""}
                  onChange={(e) => updateItem("achievements", i, { date: e.target.value })}
                />
                <Input
                  className={inputClass}
                  placeholder="Titre"
                  value={a.title || ""}
                  onChange={(e) => updateItem("achievements", i, { title: e.target.value })}
                />
                <Input
                  className={inputClass}
                  placeholder="Description"
                  value={a.description || ""}
                  onChange={(e) => updateItem("achievements", i, { description: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  onClick={() => removeItem("achievements", i)}
                  data-testid={`remove-achievement-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="add-achievement"
              onClick={() =>
                addItem("achievements", { id: crypto.randomUUID(), date: "", title: "", description: "" })
              }
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter un trophée
            </Button>
          </div>

          {motor && (
            <>
              <SectionTitle>Palmarès de courses</SectionTitle>
              <p className="text-zinc-500 font-body text-sm -mt-4">
                Les résultats course par course du pilote : épreuve, position et points.
                Affichés en tableau sur sa fiche publique.
              </p>
              <div className="space-y-3">
                {(form.race_results || []).map((r, i) => (
                  <div
                    key={r.id || i}
                    className="grid sm:grid-cols-[100px,1.5fr,1fr,110px,90px,auto] gap-2 items-end bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                    data-testid={`race-result-row-${i}`}
                  >
                    <Input
                      className={inputClass}
                      placeholder="2026"
                      value={r.date || ""}
                      onChange={(e) => updateItem("race_results", i, { date: e.target.value })}
                    />
                    <Input
                      className={inputClass}
                      placeholder="GP de Genève"
                      value={r.event || ""}
                      onChange={(e) => updateItem("race_results", i, { event: e.target.value })}
                    />
                    <Input
                      className={inputClass}
                      list="race-disciplines"
                      placeholder="Karting, F4…"
                      value={r.discipline || ""}
                      onChange={(e) => updateItem("race_results", i, { discipline: e.target.value })}
                    />
                    <Input
                      className={inputClass}
                      placeholder="1er, DNF…"
                      value={r.position || ""}
                      onChange={(e) => updateItem("race_results", i, { position: e.target.value })}
                      data-testid={`race-result-position-${i}`}
                    />
                    <Input
                      className={inputClass}
                      type="number"
                      placeholder="PTS"
                      value={r.points ?? ""}
                      onChange={(e) =>
                        updateItem("race_results", i, {
                          points: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                      onClick={() => removeItem("race_results", i)}
                      data-testid={`remove-race-result-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <datalist id="race-disciplines">
                  {getSport(form.sport).positions.map((p) => (
                    <option key={p} value={p} />
                  ))}
                </datalist>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="add-race-result"
                  onClick={() =>
                    addItem("race_results", {
                      id: crypto.randomUUID(),
                      date: "",
                      event: "",
                      discipline: "",
                      position: "",
                      points: null,
                    })
                  }
                  className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" /> Ajouter une course
                </Button>
              </div>
            </>
          )}

          {/* Ordre des sections */}
          <div className="space-y-3">
            <h3 className="font-display text-lg font-bold uppercase tracking-wide">
              Ordre des sections de la page
            </h3>
            <SectionOrderCard
              page="player"
              value={form.section_order}
              onChange={(v) => set("section_order", v)}
            />
          </div>

          {/* Actions */}
          <div className="sticky-cta">
            <div className="bg-[#0A0B0E]/90 backdrop-blur-xl border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="delete-player"
                  onClick={onDelete}
                  className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-player"
                className="text-white rounded-md px-6 h-11 font-body font-medium hover:opacity-90"
                style={{ backgroundColor: form.primary_color || "#0066FF" }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer le joueur"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
